/**
 * MarketplaceDashboard - Dashboard principal do marketplace
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Navegação por abas: Navegar Produtos, Vender Produto, Meus Anúncios
 * - Tooltip dinâmico baseado na aba ativa
 * - Modal de exemplo
 * - Listagem de produtos próprios com ações
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - ExampleModal (componente extraído)
 * - MarketplaceBrowser (componente extraído)
 * - MarketplaceForm (componente extraído)
 * - MarketplaceItemCard (componente extraído)
 */

import React, { useState } from "react";
import { useTranslation } from "../translations";
import ExampleModal from "./ExampleModal";
import MarketplaceBrowser from "./MarketplaceBrowser";
import MarketplaceForm from "./MarketplaceForm";
import MarketplaceItemCard from "./MarketplaceItemCard";

const MarketplaceDashboard = ({ items, myItems, onUpdate }) => {
  const [activeMarketplaceTab, setActiveMarketplaceTab] = useState('browse');
  const [showExample, setShowExample] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const { t } = useTranslation();

  // Tooltip dinâmico baseado na aba ativa
  const getTooltipForTab = () => {
    switch (activeMarketplaceTab) {
      case 'browse':
        return t('tooltipMarketplaceBrowse');
      case 'sell':
        return t('tooltipMarketplaceSell');
      case 'my-items':
        return t('tooltipMarketplaceMyItems');
      default:
        return t('tooltipMarketplaceBrowse');
    }
  };

  return (
    <div className="space-y-6">
      {/* Modal de Exemplo */}
      <ExampleModal isOpen={showExample} onClose={() => setShowExample(false)} type="marketplace" />

      {/* Dica de Como Usar - Dinâmica por Aba */}
      {showTip && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-amber-400 hover:text-amber-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-3">
            <span className="text-2xl">🛒</span>
            <div>
              <h4 className="font-semibold text-amber-800">{t('howToUse')}</h4>
              <p className="text-sm text-amber-700 mt-1">{getTooltipForTab()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 items-center">
          <button
            onClick={() => setActiveMarketplaceTab('browse')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeMarketplaceTab === 'browse'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('browseProducts')}
          </button>
          <button
            onClick={() => setActiveMarketplaceTab('sell')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeMarketplaceTab === 'sell'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('sellProduct')}
          </button>
          <button
            onClick={() => setActiveMarketplaceTab('my-items')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeMarketplaceTab === 'my-items'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('myAds')}
          </button>
          
          {/* Botão Ver Exemplo */}
          <button
            onClick={() => setShowExample(true)}
            className="ml-auto py-2 px-3 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1"
          >
            💡 {t('seeExample')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeMarketplaceTab === 'browse' && (
        <MarketplaceBrowser items={items} />
      )}

      {activeMarketplaceTab === 'sell' && (
        <MarketplaceForm onUpdate={onUpdate} />
      )}

      {activeMarketplaceTab === 'my-items' && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{t('myAdvertisedProducts')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {myItems.map((item) => (
              <MarketplaceItemCard key={item.id} item={item} isOwner={true} onUpdate={onUpdate} />
            ))}
          </div>
          {myItems.length === 0 && (
            <p className="text-gray-500 text-center py-8">{t('noProductsYet')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketplaceDashboard;
